import PropTypes from 'prop-types';
import CONSTANTS from './constants';

export const ClientProp = PropTypes.shape({
  id: PropTypes.number.isRequired,
  tenant: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  designation: PropTypes.oneOf([CONSTANTS.CLIENT.DESIGNATIONS]).isRequired,
  billingDay: PropTypes.number.isRequired,
  billingNote: PropTypes.string,
});
/**
 * @typedef Client
 * @type {object}
 * @property id {number}
 * @property tenant {string} - string UUID
 * @property name {string}
 * @property designation {'commercial'|'internal'}
 * @property billingDay {number}
 * @property billingNote {string=}
 */

export const DemoPayload = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  email: PropTypes.string,
}).isRequired;
/**
 * @typedef DemoPayload
 * @type {object}
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} address
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {string} email
 */

export const TrackingEntry = PropTypes.shape({
  id: PropTypes.string,
  tenant: PropTypes.string,
  name: PropTypes.string,
  cached: PropTypes.oneOf([null, PropTypes.bool]),
  count: PropTypes.number,
  meta: PropTypes.oneOf([null, PropTypes.number]),
  period: PropTypes.string,
  // address: PropTypes.string,
  // city: PropTypes.string,
  // email: PropTypes.string,
  // meta: PropTypes.string,
  // period: PropTypes.string,
  // state: PropTypes.string,
  // zip: PropTypes.string,
}).isRequired;
/**
 * @typedef TrackingEntry
 * @type {object}
 * @property {string} id
 * @property {boolean} cached
 * @property {number} count
 * @property {string|null} meta
 * @property {string} period
 */

export const ApiResponseProp = PropTypes.oneOf([
  CONSTANTS.API.STARTED,
  CONSTANTS.API.FAILED,
  CONSTANTS.API.SUCCEEDED,
  null,
]).isRequired;

export const YearDetailProp = PropTypes.shape({
  lr: PropTypes.arrayOf(PropTypes.number),
  totalLR: PropTypes.number,
  policies: PropTypes.arrayOf(PropTypes.number).isRequired,
  sumPolicies: PropTypes.number.isRequired,
  premium: PropTypes.arrayOf(PropTypes.number).isRequired,
  sumPremium: PropTypes.number.isRequired,
  loss: PropTypes.arrayOf(PropTypes.number).isRequired,
  sumLoss: PropTypes.number.isRequired,
  net: PropTypes.arrayOf(PropTypes.number),
  sumNet: PropTypes.number,
});
/**
 * @typedef YearDetail
 * @type {object}
 * @property {?number[]|?math.Matrix} lr
 * @property {?number|?math.MathScalarType} totalLR
 * @property {?number[]|?math.Matrix} policies
 * @property {?number|?math.MathScalarType} sumPolicies
 * @property {?number[]|?math.Matrix} premium
 * @property {?number|?math.MathScalarType} sumPremium
 * @property {?number[]|?math.Matrix} loss
 * @property {?number|?math.MathScalarType} sumLoss
 * @property {?number[]|?math.Matrix} net
 * @property {?number|?math.MathScalarType} sumNet
 */

export const RoiCalcStateProp = PropTypes.shape({
  acquisitionDistribution: PropTypes.arrayOf(PropTypes.number).isRequired,
  acquisitionRate: PropTypes.number,
  averagePremium: PropTypes.number,
  baselineLift: PropTypes.arrayOf(PropTypes.number),
  carrierNonRenewalRate: PropTypes.number,
  combinedLift: PropTypes.arrayOf(PropTypes.number),
  hasBaselineModel: PropTypes.bool,
  nonRenewalDistribution: PropTypes.arrayOf(PropTypes.number).isRequired,
  policiesInForce: PropTypes.number,
  riskCost: PropTypes.number,
  yearlyCancellationRate: PropTypes.number,
  yearlyRates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
});
/**
 * @typedef RoiCalcState
 * @type {object}
 * @property {number[]} acquisitionDistribution
 * @property {number} acquisitionRate
 * @property {number} averagePremium
 * @property {number[]} baselineLift
 * @property {number} carrierNonRenewalRate
 * @property {number[]} combinedLift
 * @property {boolean} hasBaselineModel
 * @property {number[]} nonRenewalDistribution
 * @property {number} policiesInForce
 * @property {number} riskCost
 * @property {number} yearlyCancellationRate
 * @property {number[][]} yearlyRates
 */

export const RoiCalcSeedProp = PropTypes.shape({
  avg_yearly_direct_loss_cost: PropTypes.number.isRequired,
  avg_yearly_premium: PropTypes.number.isRequired,
  cancel_rate: PropTypes.number.isRequired,
  new_business_rate: PropTypes.number.isRequired,
  non_renewal_rate: PropTypes.number.isRequired,
  policies_in_force: PropTypes.number.isRequired,
});
/**
 * @typedef RoiCalcSeed
 * @type {object}
 * @property {number} avg_yearly_direct_loss_cost
 * @property {number} avg_yearly_premium
 * @property {number} cancel_rate
 * @property {number} new_business_rate
 * @property {number} non_renewal_rate
 * @property {number} policies_in_force
 */

export const FairnessProp = PropTypes.shape({
  method: PropTypes.oneOf(['mean', 'median']).isRequired,
  upper_bound: PropTypes.oneOf([null, undefined, PropTypes.number]),
  upperBound: PropTypes.oneOf([null, undefined, PropTypes.number]),
  lower_bound: PropTypes.oneOf([null, undefined, PropTypes.number]),
  lowerBound: PropTypes.oneOf([null, undefined, PropTypes.number]),
  treatment_type: PropTypes.oneOf([null, undefined, 'all', 'age', 'sex', 'race']),
  treatmentType: PropTypes.oneOf([null, undefined, 'all', 'age', 'sex', 'race']),
});
/**
 * @typedef Fairness
 * @type {object}
 * @property {'mean'|'median'} method - either mean or median calculation
 * @property {number=} upper_bound
 * @property {number=} lower_bound
 * @property {number=} upperBound
 * @property {number=} lowerBound
 * @property {null|undefined|'all'|'age'|'sex'|'race'} treatment_type
 * @property {null|undefined|'all'|'age'|'sex'|'race'} treatmentType
 */

export const LiftScoreProp = PropTypes.arrayOf(
  PropTypes.shape({
    b_lift: PropTypes.arrayOf(PropTypes.number),
    b_premium: PropTypes.arrayOf(PropTypes.number),
    b_risk_cost: PropTypes.arrayOf(PropTypes.number),
    b_pif_percentile: PropTypes.arrayOf(PropTypes.number),
    p_lift: PropTypes.arrayOf(PropTypes.number),
    p_premium: PropTypes.arrayOf(PropTypes.number),
    p_risk_cost: PropTypes.arrayOf(PropTypes.number),
    p_pif_percentile: PropTypes.arrayOf(PropTypes.number),
    min_probability: PropTypes.arrayOf(PropTypes.number),
    avg_premium: PropTypes.arrayOf(PropTypes.number),
  })
);
/**
 * @typedef LiftScore
 * @type {object}
 * @property {number[]} b_lift
 * @property {number[]} b_premium
 * @property {number[]} b_risk_cost
 * @property {number[]} b_pif_percentile
 * @property {number[]} p_lift
 * @property {number[]} p_premium
 * @property {number[]} p_risk_cost
 * @property {number[]} p_pif_percentile
 * @property {number[]} min_probability
 * @property {number[]} avg_premium
 */

const ModelBaseObj = {
  id: PropTypes.number,
  name: PropTypes.string,
  isDeleted: PropTypes.bool,
  payload: PropTypes.shape({
    version: PropTypes.string.isRequired,
  }),
  modelGroupId: PropTypes.number,
  predictionType: PropTypes.string,
  dataModelType: PropTypes.string,
  framework: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    refreshes: PropTypes.arrayOf(PropTypes.string),
    regulationTags: PropTypes.arrayOf(PropTypes.string),
  }),
  typeDetail: PropTypes.string,
  modelData: PropTypes.shape({
    dataPayload: PropTypes.shape({
      lift_scores: LiftScoreProp,
      equal_persons: PropTypes.shape({
        lift_scores: LiftScoreProp,
      }),
      equal_premium: PropTypes.shape({
        lift_scores: LiftScoreProp,
      }),
      equal_premiums: PropTypes.shape({
        lift_scores: LiftScoreProp,
      }),
      risk_percentage: PropTypes.number,
      pets_percentage: PropTypes.number,
      positiveSegment: PropTypes.shape({
        matched: PropTypes.number,
      }),
      negativeSegment: PropTypes.shape({
        matched: PropTypes.number,
      }),
      validationSegment: PropTypes.shape({
        matched: PropTypes.number,
      }),
      actuarialSegment: PropTypes.shape({
        dataset: PropTypes.shape({
          payload: PropTypes.shape({
            match_zero_claims: PropTypes.number,
            match_gte_one_claims: PropTypes.number,
            onb_zero_claims: PropTypes.number,
            onb_gte_one_claims: PropTypes.number,
          }),
        }),
      }),
    }),
    metadataPayload: PropTypes.shape({
      shared: PropTypes.bool,
      fairness: FairnessProp,
    }),
    lifecycle_metadata: PropTypes.shape({
      APPROVED: PropTypes.oneOf([
        undefined,
        PropTypes.shape({
          internal_validation_percentage: PropTypes.oneOf([undefined, PropTypes.number]),
          internalValidationPercentage: PropTypes.oneOf([undefined, PropTypes.number]),
        }),
      ]),
    }),
    lifecycleMetadata: PropTypes.shape({
      APPROVED: PropTypes.oneOf([
        undefined,
        PropTypes.shape({
          internal_validation_percentage: PropTypes.oneOf([undefined, PropTypes.number]),
          internalValidationPercentage: PropTypes.oneOf([undefined, PropTypes.number]),
        }),
      ]),
    }),
  }),
};

export const ModelProp = PropTypes.shape({
  ...ModelBaseObj,
  modelHead: PropTypes.shape(ModelBaseObj),
  modelGroup: PropTypes.arrayOf(PropTypes.shape(ModelBaseObj)),
});

export const RtCsProp = PropTypes.shape({
  id: PropTypes.string,
  description: PropTypes.string,
  score_type: PropTypes.string,
  recentStatus: PropTypes.string,
  recent_status: PropTypes.string,
  config_metadata: PropTypes.shape({
    model: ModelProp,
    data_model_type: PropTypes.string,
    type_detail: PropTypes.string,
    prediction_type: PropTypes.string,
  }),
}).isRequired;
/**
 * @typedef RtCs
 * @type {object}
 * @property {string} id
 * @property {string} description
 * @property {object} config_metadata
 * @property {string} score_type
 */

export const DialogActionProp = PropTypes.shape({
  cancel: PropTypes.shape({
    before: PropTypes.oneOfType([undefined, PropTypes.func]),
    on: PropTypes.func.isRequired,
    after: PropTypes.oneOfType([undefined, PropTypes.func]),
  }).isRequired,
  close: PropTypes.shape({
    before: PropTypes.oneOfType([undefined, PropTypes.func]),
    on: PropTypes.func.isRequired,
    after: PropTypes.oneOfType([undefined, PropTypes.func]),
  }).isRequired,
  handlers: PropTypes.arrayOf(
    PropTypes.shape({
      before: PropTypes.oneOfType([undefined, PropTypes.func]),
      on: PropTypes.func.isRequired,
      after: PropTypes.oneOfType([undefined, PropTypes.func]),
    })
  ),
});

export const HistoryProp = PropTypes.shape({
  push: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
});
/**
 * @typedef History
 * @type {object}
 * @property {function} push
 * @property {function} replace
 */

export const MatchProp = PropTypes.shape({
  params: PropTypes.shape({
    modelClass: PropTypes.string,
    modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
}).isRequired;
/**
 * @typedef Match
 * @type {object}
 * @property {string} params.modelClass
 * @property {string|number} params.modelId
 */

export const LocationProp = PropTypes.shape({
  search: PropTypes.string,
}).isRequired;
/**
 * @typedef Location
 * @type {object}
 * @property {string} search
 */

/**
 * @typedef ModelCopyPayload
 * @type {object}
 * @property {string} [newName] - the new name of the model in the client it's copying to
 * @property {number} modelId - the ID of the model to copy
 * @property {string} targetTenant - the client to copy the model to
 * @property {string} sourceTenant - the client to copy the model from (since this is an admin call)
 */
